/**
 * alpacaImportApi.js
 *
 * Dedicated API service for the Alpaca trade history import HTTP calls.
 * Every function logs the outgoing request and response status to the browser
 * console. Errors are caught, logged with detail, and rethrown with a
 * consistent { message, detail, status } structure so callers can display
 * meaningful error messages without inspecting raw HTTP responses.
 */

import apiClient from './apiClient'

const BASE = '/admin/alpaca/import-history';

/**
 * Normalise an axios error into a consistent shape.
 * @param {string} context - Human-readable description of the operation.
 * @param {Error} axiosError - The error thrown by apiClient.
 * @returns {Error}
 */
function buildError(context, axiosError) {
  const response = axiosError.response ?? null;
  const status = response ? response.status : 'network-error';
  const body = response ? response.data : axiosError;
  const detail =
    (body && body.detail) ||
    (body && body.message) ||
    (body instanceof Error ? body.message : axiosError.message) ||
    'Unknown error';
  const message = `[alpacaImportApi] ${context} failed (HTTP ${status}): ${detail}`;
  console.error(message, { status, body });
  const err = new Error(message);
  err.detail = detail;
  err.status = status;
  return err;
}

/**
 * Trigger an Alpaca trade history import (dry-run or live).
 *
 * @param {Object} params
 * @param {number} params.lookback_days - Number of days of history to fetch (1–365).
 * @param {boolean} params.dry_run - When true, previews without writing to DB.
 * @returns {Promise<Object>} Object with `total_fetched`, `inserted`, `skipped_not_filled`,
 *   `skipped_already_exists`, and `errors`.
 */
export async function importTradeHistory({ lookback_days, dry_run }) {
  console.log(`[alpacaImportApi] POST ${BASE}/`, { lookback_days, dry_run });
  try {
    const response = await apiClient.post(`${BASE}/`, { lookback_days, dry_run });
    console.log(`[alpacaImportApi] POST ${BASE}/ → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    const status = err.response?.status ?? 'network-error';
    console.log(`[alpacaImportApi] POST ${BASE}/ → HTTP ${status}`);
    throw buildError('importTradeHistory', err);
  }
}
